import React from 'react';
import { fromPairs, map, get, isEqual, cloneDeep, find } from 'lodash';
import classnames from 'classnames';

// local Dependencies
import { getLabel } from 'utils/getLabel';
import { Icon } from 'modules/icon-bank';
import { Form } from 'modules/form';
import { button as Button } from 'modules/button';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { personalIdWidget as PersonalIdWidget } from 'ui/views/widgets/personalIdWidget';
 *
 * // render
 * <PersonalIdWidget propName='propValue'/>
 */

/* ------------------ */

// editable mode of the form
const PersonalIdWidgetEditable = ( props ) => {

    return (
        <Form
            fields={ fromPairs( map( props.fields, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
            values={ get( props, 'data.state.values' ) } // default values
            mode = { 'form' }
            onChangeCallBack={ props.onChangeCallBack }
            updatFieldsOnChange={ true }
            key={ get( props, 'data.state.values.categoryType' ) }
            onSubmit={ ( { values, isValid } ) => {
                if( isValid ) {
                    props.onFormSubmit( values );
                }
            } }

            // isValid={ false }
        >
            { ( { fields, values, /* isValid, */ errors, onChange, onSumbit } = {} ) => {

                return (
                    <form
                        onSubmit={ onSumbit }
                        className='ui-personal-id-widget__body__form'
                    >
                        { /* fields */ }
                        { map( fields, ( field ) => {

                            if( get( field, 'config.hide' ) ) {
                                return null;
                            }

                            const fieldClassName = classnames( 'ui-personal-id-widget__body__form__field', `ui-personal-id-widget__body__form__${field.name}` );

                            return (
                                <field.view
                                    className={ fieldClassName }
                                    key={ field.name }
                                    name={ field.name }
                                    required={ field.required }
                                    placeholder={ field.placeholder }
                                    label={ field.title }
                                    onChange={ onChange }
                                    value={ values[ field.name ] }
                                    error={ errors[ field.name ] }
                                    options={ get( props.data.options, field.options ) }
                                    config={ field.config }
                                    mask={ field.mask }
                                />
                            );
                        } ) }
                        
                        {/* button actions */}
                        <div className='ui-personal-id-widget__body__form__actions'>
                            { map( props.actions, action => {
                                return (
                                    !isEqual( action.name, 'edit' ) && !isEqual( action.name, 'close' ) ?
                                        <Button
                                            key={ action.name }
                                            id= { action.name }
                                            type= { 'cancel' !== action.name ? 'submit' : 'button' }
                                            disabled={ 'cancel' !== action.name ? props.disableSubmit : false }
                                            outline={ 'cancel' === action.name ? true: false }
                                            size='large'
                                            className='ui-personal-id-widget__body__form__actions__action'
                                            onClick = { isEqual( 'cancel', action.name ) ?  () => {
                                                props.closeOverlay();
                                            } : null }
                                            { ...action }
                                        />
                                    : null
                                );
                            } ) }
                        </div>
                    </form>
                );
            } }
        </Form>
    );
};

// set display name
PersonalIdWidgetEditable.displayName = 'personalIdWidget.PersonalIdWidgetEditable';

/* ------------------ */

// readonly mode of the form
const PersonalIdWidgetReadOnly = ( props ) => {

    // changing the title of the field for view Data form
    const _fields = cloneDeep( props.fields );
    const idNumberField = find( _fields, { name: 'idNumber' } );
    idNumberField.title = idNumberField.viewTitle;

	return (
		<Form
            fields={ fromPairs( map( _fields, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
            values={ get( props, 'data.state.values' ) } // default values
            mode = { 'form' }
            onChangeCallBack={ props.onChangeCallBack }
            updatFieldsOnChange={ true }
            key={ get( props, 'data.state.values.categoryType' ) }
            onSubmit={ ( { values, isValid } ) => {
                if( isValid ) {
                    props.onFormSubmit( values );
                }
            } }

            // isValid={ false }
        >
            { ( { fields, values, /* isValid, */ errors, onChange, onSumbit } = {} ) => {

                return (
                    <>
                        <form
                            onSubmit={ onSumbit }
                            className='ui-personal-id-widget__body__form-readonly'
                        >
                            { /* fields */ }
                            { map( fields, ( field ) => {

                                if( get( field, 'config.hide' ) ) {
                                    return null;
                                }

                                const fieldClassName = classnames( 'ui-personal-id-widget__body__form-readonly__field', `ui-personal-id-widget__body__form-readonly__${field.name}` );

                                return (
                                    <field.view
                                        className={ fieldClassName }
                                        key={ field.name }
                                        name={ field.name }
                                        required={ field.required }
                                        placeholder={ field.placeholder }
                                        label={ field.title }
                                        onChange={ onChange }
                                        value={ values[ field.name ] }
                                        error={ errors[ field.name ] }
                                        options={ get( props.data.options, field.options ) }
                                        config={ field.config }
                                        mask={ field.mask }
                                        mode='readonly'
                                    />
                                );
                            } ) }
                        </form>
                        {/* button actions */}
                        <div className='ui-personal-id-widget__body__form-readonly__actions'>
                            { map( props.actions, action => {
                                return (
                                    !isEqual( action.name, 'submit' ) && !isEqual( action.name, 'cancel' ) ?
                                        <Button
                                            key={ action.name }
                                            id= { action.name }
                                            type= { 'close' !== action.name ? 'submit' : 'button' }
                                            outline={ 'close' === action.name ? true: false }
                                            size='large'
                                            className='ui-personal-id-widget__body__form-readonly__actions__action'
                                            onClick = { isEqual( 'close', action.name ) ?  () => {
                                                props.closeOverlay();
                                            } : () => props.onFormEdit() }
                                            { ...action }
                                        />
                                    : null
                                );
                            } ) }
                        </div>
                    </>
                );
            } }
        </Form>
	);
};

// set display name
PersonalIdWidgetReadOnly.displayName = 'personalIdWidget.PersonalIdWidgetReadOnly';

/* ------------------ */
export const personalIdWidget = ( props ) => {
    return (
        <div className='ui-personal-id-widget'>
			<div className='ui-personal-id-widget__header'>

				{ /* title for the modal */ }
				<div className='ui-personal-id-widget__header__title'>
					{ getLabel( props.title ) }
				</div>

				{ /* close button */ }
				<div
					className='ui-error-overlay__head__close'
					onClick={ () => props.closeOverlay() }
				>
					<Icon name='close' width='14' height='14' fill='#ffffff' />
				</div>
			</div>
			<div className='ui-personal-id-widget__body' >
                { props.isEditMode ? ( <PersonalIdWidgetEditable { ...props }/> ) : ( <PersonalIdWidgetReadOnly { ...props }/> ) }
			</div>
		</div>
    );
};

// set display name
personalIdWidget.displayName = 'personalIdWidget';

// set default props
personalIdWidget.defaultProps = {};

// prop types
personalIdWidget.propTypes = {};
